import React, { useState, useEffect } from 'react';
import { Save, Key, Eye, EyeOff, CheckCircle, AlertCircle, Loader, ExternalLink, HelpCircle, X, ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import apiClient from '../services/api/client';
import { MARKETPLACE_LOGOS } from '../assets/marketplace-logos';

interface MarketplaceCredentials {
  // Mercado Livre — campos do formulário (camelCase)
  tokenMercadoLivre?: string;
  clientIdMercadoLivre?: string;
  clientSecretMercadoLivre?: string;
  userIdMercadoLivre?: string;
  // ML — campos gerados pelo OAuth (lidos do banco, não editáveis)
  mlAccessToken?: string;
  mlRefreshToken?: string;
  mlTokenExpiry?: number;
  mlUserId?: string;
  mlConnectedAt?: string;

  // Magazine Luiza — OAuth
  magaluAccessToken?: string;
  magaluRefreshToken?: string;
  magaluTokenExpiry?: number;
  magaluConnectedAt?: string;

  tokenMagalu?: string;
  apiKeyMagalu?: string;
  sellerIdMagalu?: string;

  tokenCasasBahia?: string;
  clientIdCasasBahia?: string;
  sellerIdCasasBahia?: string;

  // Decathlon / Mirakl
  decathlonApiKey?: string;
  decathlonMiraklUrl?: string;

  tokenCarrefour?: string;
  apiKeyCarrefour?: string;
  shopIdCarrefour?: string;

  tokenLeroyMerlin?: string;
  apiKeyLeroyMerlin?: string;
  shopIdLeroyMerlin?: string;

  // Amazon — OAuth
  amazonSellerId?: string;
  amazonMarketplaceId?: string;
  amazonConnectedAt?: string;

  // Shopee — OAuth (salvo pelo callback)
  shopIdShopee?: string;
  shopeeAccessToken?: string;
  shopeeRefreshToken?: string;
  shopeeTokenExpiry?: number;
  shopeeConnectedAt?: string;

  // TikTok Shop — OAuth
  tiktokAccessToken?: string;
  tiktokRefreshToken?: string;
  tiktokAccessTokenExpiry?: number;
  tiktokShopId?: string;
  tiktokShopCipher?: string;
  tiktokSellerName?: string;

  // AliExpress — OAuth
  aliexpressAccessToken?: string;
  aliexpressRefreshToken?: string;
  aliexpressTokenExpiry?: number;
  aliexpressSellerId?: string;
  aliexpressConnectedAt?: string;

  // index para campos extras do banco
  [key: string]: string | number | undefined;
}

const MARKETPLACES = [
  // ==========================================
  // GIGANTES DE VOLUME
  // ==========================================
  {
    id: 'ml',
    name: 'Mercado Livre',
    logo: 'ml',
    color: '#FFE600',
    category: 'Gigantes',
    registrationUrl: 'https://global-selling.mercadolibre.com/',
    fields: [
      { key: 'userIdMercadoLivre', label: 'User ID (ID do Vendedor)', type: 'text', required: true },
      { key: 'clientIdMercadoLivre', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecretMercadoLivre', label: 'Client Secret', type: 'password', required: true },
      { key: 'tokenMercadoLivre', label: 'Access Token', type: 'password', required: true },
    ]
  },
  {
    id: 'shopee',
    name: 'Shopee',
    logo: 'shopee',
    color: '#EE4D2D',
    category: 'Gigantes',
    registrationUrl: 'https://seller.shopee.com.br/account/register',
    fields: []
  },
  {
    id: 'amazon',
    name: 'Amazon',
    logo: 'amazon',
    color: '#FF9900',
    category: 'Gigantes',
    registrationUrl: 'https://venda.amazon.com.br/',
    fields: [
      { key: 'amazonSellerId', label: 'Seller ID (preenchido automaticamente via OAuth)', type: 'text', required: false },
      { key: 'amazonMarketplaceId', label: 'Marketplace ID', type: 'text', required: false },
    ]
  },
  {
    id: 'magalu',
    name: 'Magazine Luiza',
    logo: 'magalu',
    color: '#0086FF',
    category: 'Gigantes',
    registrationUrl: 'https://seller.magalu.com/',
    fields: []
  },
  {
    id: 'shein',
    name: 'Shein',
    logo: 'shein',
    color: '#222222',
    category: 'Gigantes',
    registrationUrl: 'https://sellershein.com.br/',
    fields: [
      { key: 'sheinAppId', label: 'App ID', type: 'text', required: true },
      { key: 'sheinSecretKey', label: 'Secret Key', type: 'password', required: true },
    ]
  },
  {
    id: 'tiktok',
    name: 'TikTok Shop',
    logo: 'tiktok',
    color: '#010101',
    category: 'Gigantes',
    registrationUrl: 'https://partner.tiktokshop.com/',
    fields: []
  },
  {
    id: 'aliexpress',
    name: 'AliExpress',
    logo: 'aliexpress',
    color: '#FF4747',
    category: 'Gigantes',
    registrationUrl: 'https://sell.aliexpress.com/',
    fields: []
  },

  // ==========================================
  // GRUPO CASAS BAHIA (Via Varejo)
  // Ponto Frio e Extra compartilham as mesmas credenciais do Grupo Casas Bahia
  // ==========================================
  {
    id: 'casasbahia',
    name: 'Casas Bahia',
    logo: 'casasbahia',
    color: '#0057A8',
    category: 'Grupo Casas Bahia',
    registrationUrl: 'https://so.via.com.br/',
    fields: [
      { key: 'tokenCasasBahia', label: 'Access Token (Grupo Casas Bahia)', type: 'password', required: true },
      { key: 'casasSellerId',   label: 'Seller ID', type: 'text', required: false },
    ]
  },
  {
    id: 'pontofrio',
    name: 'Ponto Frio',
    logo: 'pontofrio',
    color: '#0070CC',
    category: 'Grupo Casas Bahia',
    registrationUrl: 'https://so.via.com.br/',
    fields: [
      { key: 'tokenCasasBahia', label: 'Access Token (Grupo Casas Bahia)', type: 'password', required: true },
      { key: 'casasSellerId',   label: 'Seller ID', type: 'text', required: false },
    ]
  },
  {
    id: 'extra',
    name: 'Extra',
    logo: 'extra',
    color: '#E30613',
    category: 'Grupo Casas Bahia',
    registrationUrl: 'https://so.via.com.br/',
    fields: [
      { key: 'tokenCasasBahia', label: 'Access Token (Grupo Casas Bahia)', type: 'password', required: true },
      { key: 'casasSellerId',   label: 'Seller ID', type: 'text', required: false },
    ]
  },

  // ==========================================
  // CASA E CONSTRUÇÃO
  // ==========================================
  {
    id: 'leroy',
    name: 'Leroy Merlin',
    logo: 'leroy',
    color: '#78BE20',
    category: 'Casa e Construção',
    registrationUrl: 'https://www.leroymerlin.com.br/marketplace',
    fields: [
      { key: 'leroyApiKey', label: 'API Key', type: 'password', required: true },
    ]
  },
  {
    id: 'carrefour',
    name: 'Carrefour',
    logo: 'carrefour',
    color: '#005AA9',
    category: 'Supermercados',
    registrationUrl: 'https://www.carrefour.com.br/marketplace',
    fields: [
      { key: 'carrefourToken', label: 'API Token (Mirakl)', type: 'password', required: true },
    ]
  },

  // ==========================================
  // MODA E ESPORTE
  // ==========================================
  {
    id: 'netshoes',
    name: 'Netshoes',
    logo: 'netshoes',
    color: '#5A2D82',
    category: 'Moda e Esporte',
    registrationUrl: 'https://www.netshoes.com.br/marketplace',
    fields: [
      { key: 'netshoesClientId', label: 'Client ID', type: 'text', required: true },
      { key: 'netshoesAccessToken', label: 'Access Token', type: 'password', required: true },
    ]
  },
  {
    id: 'dafiti',
    name: 'Dafiti',
    logo: 'dafiti',
    color: '#000000',
    category: 'Moda e Esporte',
    registrationUrl: 'https://marketplace.dafiti.com.br/',
    fields: [
      { key: 'dafitiUserId', label: 'User ID (E-mail)', type: 'text', required: true },
      { key: 'dafitiApiKey', label: 'API Key', type: 'password', required: true },
    ]
  },
];

function MarketplaceLogo({ logoKey, name, color }: { logoKey: string; name: string; color: string }) {
  const [error, setError] = useState(false);
  const url = MARKETPLACE_LOGOS[logoKey];

  if (!url || error) {
    return (
      <span className="text-xl font-bold" style={{ color }}>
        {name[0]}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={name}
      className="w-full h-full object-contain rounded-lg"
      onError={() => setError(true)}
    />
  );
}

export default function Settings() {
  const [credentials, setCredentials] = useState<MarketplaceCredentials>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState<{ [key: string]: boolean }>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [userCode, setUserCode] = useState('');
  const [testStatus, setTestStatus] = useState<{ [id: string]: 'idle' | 'loading' | 'success' | 'error' }>({});
  const [showHelp, setShowHelp] = useState(false);
  const [amazonOAuthStatus, setAmazonOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [amazonOAuthError, setAmazonOAuthError] = useState('');
  const [magaluOAuthStatus, setMagaluOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [mlOAuthStatus, setMlOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [mlOAuthError, setMlOAuthError] = useState('');
  const [shopeeOAuthStatus, setShopeeOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [shopeeOAuthError, setShopeeOAuthError] = useState('');
  const [tiktokOAuthStatus, setTiktokOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [tiktokOAuthError, setTiktokOAuthError] = useState('');
  const [aliexpressOAuthStatus, setAliexpressOAuthStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [aliexpressOAuthError, setAliexpressOAuthError] = useState('');

  const isMarketplaceConfigured = (marketplace: typeof MARKETPLACES[0]) => {
    if (marketplace.id === 'magalu') {
      return !!(magaluOAuthStatus === 'success' || credentials.magaluAccessToken);
    }
    if (marketplace.id === 'amazon') {
      return !!(credentials.amazonSellerId || credentials.amazonConnectedAt);
    }
    if (marketplace.id === 'ml') {
      if (mlOAuthStatus === 'success' || credentials.mlAccessToken) return true;
      return marketplace.fields.every(f => !!credentials[f.key]?.trim());
    }
    if (marketplace.id === 'shopee') {
      return !!(shopeeOAuthStatus === 'success' || credentials.shopeeAccessToken || credentials.shopIdShopee);
    }
    if (marketplace.id === 'tiktok') {
      return !!(tiktokOAuthStatus === 'success' || credentials.tiktokAccessToken);
    }
    if (marketplace.id === 'aliexpress') {
      return !!(aliexpressOAuthStatus === 'success' || credentials.aliexpressAccessToken);
    }
    if (marketplace.fields.length === 0) return false;
    return marketplace.fields.every(f => !!credentials[f.key]?.trim());
  };

  useEffect(() => {
    // Pegar userCode do localStorage ou de onde você armazena
    const storedUserCode = localStorage.getItem('userCode') || 'TEST_USER';
    setUserCode(storedUserCode);

    // Carregar credenciais existentes
    loadCredentials(storedUserCode);

    // Detectar retorno dos OAuth callbacks
    const params = new URLSearchParams(window.location.search);

    if (params.get('amazon_connected') === 'true') {
      setAmazonOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadCredentials(storedUserCode), 500);
    } else if (params.get('amazon_error')) {
      setAmazonOAuthError(params.get('amazon_error') || 'Erro desconhecido');
      setAmazonOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('magalu_connected') === 'true') {
      setMagaluOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('magalu_error')) {
      setMagaluOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('ml_connected') === 'true') {
      setMlOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadCredentials(storedUserCode), 500);
    } else if (params.get('ml_error')) {
      setMlOAuthError(params.get('ml_error') || 'Erro desconhecido');
      setMlOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('shopee_connected') === 'true') {
      setShopeeOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadCredentials(storedUserCode), 500);
    } else if (params.get('shopee_error')) {
      setShopeeOAuthError(params.get('shopee_error') || 'Erro desconhecido');
      setShopeeOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('tiktok_connected') === 'true') {
      setTiktokOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadCredentials(storedUserCode), 500);
    } else if (params.get('tiktok_error')) {
      setTiktokOAuthError(params.get('tiktok_error') || 'Erro desconhecido');
      setTiktokOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('aliexpress_connected') === 'true') {
      setAliexpressOAuthStatus('success');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadCredentials(storedUserCode), 500);
    } else if (params.get('aliexpress_error')) {
      setAliexpressOAuthError(params.get('aliexpress_error') || 'Erro desconhecido');
      setAliexpressOAuthStatus('error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadCredentials = async (userCode: string) => {
    setLoading(true);
    try {
      // apiClient usa URL absoluta (VITE_API_BASE_URL) — funciona em dev e produção
      const response = await apiClient.get('/settings/credentials', {
        params: { empresa: userCode },
      });
      setCredentials(response.data);
    } catch (error) {
      console.error('Erro ao carregar credenciais:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');

    try {
      await apiClient.post('/settings/credentials', {
        userCode,
        credentials,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Erro ao salvar credenciais:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (fieldKey: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  const updateCredential = (key: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleShopeeConnect = async () => {
    setShopeeOAuthStatus('connecting');
    try {
      const response = await apiClient.get('/shopee/auth/url');
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      setShopeeOAuthError(err.message || 'Erro ao iniciar autorização');
      setShopeeOAuthStatus('error');
    }
  };

  const handleMLConnect = async () => {
    setMlOAuthStatus('connecting');
    try {
      const response = await apiClient.get('/ml/auth/url');
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      setMlOAuthError(err.message || 'Erro ao iniciar autorização');
      setMlOAuthStatus('error');
    }
  };

  const handleTikTokConnect = () => {
    setTiktokOAuthStatus('connecting');
    const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('userCode') || '';
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005') + '/site/api/hub';
    window.location.href = `${apiBase}/tiktok/oauth/authorize?user_code=${encodeURIComponent(tenantId)}`;
  };

  const handleMagaluConnect = () => {
    setMagaluOAuthStatus('connecting');
    // Usa tenantId (UUID) — igual ao que o apiClient usa em todas as chamadas
    const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('userCode') || '';
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005') + '/site/api/hub';
    window.location.href = `${apiBase}/magalu/auth/redirect?empresa=${encodeURIComponent(tenantId)}`;
  };

  const handleAliExpressConnect = () => {
    setAliexpressOAuthStatus('connecting');
    const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('userCode') || '';
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005') + '/site/api/hub';
    window.location.href = `${apiBase}/aliexpress/oauth/authorize?user_code=${encodeURIComponent(tenantId)}`;
  };

  const handleAmazonConnect = async () => {
    setAmazonOAuthStatus('connecting');
    try {
      // apiClient usa URL absoluta — funciona em produção (Cloud Run) e dev
      const response = await apiClient.get('/amazon/auth/url', {
        params: { empresa: userCode },
      });
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      setAmazonOAuthError(err.message || 'Erro ao iniciar autorização');
      setAmazonOAuthStatus('error');
    }
  };

  const testConnection = async (marketplaceId: string) => {
    setTestStatus(prev => ({ ...prev, [marketplaceId]: 'loading' }));
    try {
      await apiClient.post('/settings/test-connection', {
        userCode,
        marketplaceId,
        credentials,
      });
      setTestStatus(prev => ({ ...prev, [marketplaceId]: 'success' }));
      setTimeout(() => setTestStatus(prev => ({ ...prev, [marketplaceId]: 'idle' })), 4000);
    } catch {
      setTestStatus(prev => ({ ...prev, [marketplaceId]: 'error' }));
      setTimeout(() => setTestStatus(prev => ({ ...prev, [marketplaceId]: 'idle' })), 4000);
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
    );
  }

  return (
      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-dark-100 mb-2">Configurações</h1>
            <p className="text-dark-400">Gerencie as credenciais de integração com os marketplaces</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Botão de Ajuda */}
            <button
                onClick={() => setShowHelp(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-dark-100 font-medium text-sm transition-all shadow-lg shadow-blue-600/30"
                title="Como configurar?"
            >
              <HelpCircle className="w-4 h-4" />
              Ajuda
            </button>

          <button
              onClick={handleSave}
              disabled={saving}
              className={clsx(
                  'flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all shadow-lg',
                  saveStatus === 'success'
                      ? 'bg-emerald-500 text-dark-100'
                      : saveStatus === 'error'
                          ? 'bg-red-500 text-dark-100'
                          : 'bg-primary-500 hover:bg-primary-600 text-dark-100 shadow-primary-500/20',
                  saving && 'opacity-50 cursor-not-allowed'
              )}
          >
            {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  A salvar...
                </>
            ) : saveStatus === 'success' ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Salvo!
                </>
            ) : saveStatus === 'error' ? (
                <>
                  <AlertCircle className="w-5 h-5" />
                  Erro ao salvar
                </>
            ) : (
                <>
                  <Save className="w-5 h-5" />
                  Salvar Credenciais
                </>
            )}
          </button>
          </div>
        </div>

        {/* ── Painel de Ajuda (slide-in lateral) ── */}
        {showHelp && (
            <>
              {/* Overlay */}
              <div
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                  onClick={() => setShowHelp(false)}
              />

              {/* Drawer */}
              <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-900 border-l border-dark-700 z-50 flex flex-col shadow-2xl">
                {/* Header do drawer */}
                <div className="flex items-center justify-between p-6 border-b border-dark-700">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-primary-400" />
                    </div>
                    <h2 className="text-lg font-bold text-dark-100">Como configurar?</h2>
                  </div>
                  <button
                      onClick={() => setShowHelp(false)}
                      className="w-8 h-8 rounded-lg bg-dark-700 hover:bg-dark-600 flex items-center justify-center text-dark-400 hover:text-dark-100 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Conteúdo */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                  {/* Intro */}
                  <p className="text-dark-300 text-sm leading-relaxed">
                    Para integrar o QFlow com os marketplaces, você precisa fornecer as credenciais de acesso de cada um deles. É mais simples do que parece — siga os três passos abaixo.
                  </p>

                  {/* Passos */}
                  {[
                    {
                      num: '1',
                      title: 'Crie sua conta de vendedor no marketplace',
                      body: 'Se ainda não tem conta em um marketplace, clique no botão "Criar Conta no Marketplace" (destacado em amarelo) dentro do card correspondente. Você será redirecionado para a página oficial de cadastro.',
                    },
                    {
                      num: '2',
                      title: 'Acesse o painel de desenvolvedor do marketplace',
                      body: 'Após criar a conta, cada marketplace disponibiliza uma área de API ou "Integrações". Lá você encontra os campos que precisamos: Client ID, Secret, Access Token, Partner ID, etc. Copie cada valor com cuidado.',
                    },
                    {
                      num: '3',
                      title: 'Cole as credenciais aqui e salve',
                      body: 'Cole os valores nos campos correspondentes de cada marketplace. Clique em "Salvar Credenciais" no canto superior direito. Em seguida, use o botão "Testar Conexão" para confirmar que a integração está ativa e funcionando.',
                    },
                  ].map(step => (
                      <div key={step.num} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-dark-100 font-bold text-sm">
                          {step.num}
                        </div>
                        <div>
                          <p className="text-dark-100 font-semibold text-sm mb-1">{step.title}</p>
                          <p className="text-dark-400 text-sm leading-relaxed">{step.body}</p>
                        </div>
                      </div>
                  ))}

                  {/* Dica rápida */}
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                    <p className="text-amber-400 text-xs font-semibold uppercase tracking-wide mb-2">💡 Dica rápida</p>
                    <p className="text-dark-300 text-sm leading-relaxed">
                      O botão <span className="text-amber-400 font-medium">"Criar Conta no Marketplace"</span> fica destacado em amarelo enquanto as credenciais daquele marketplace ainda não foram preenchidas — use-o como guia visual para saber quais marketplaces ainda faltam configurar.
                    </p>
                  </div>

                  {/* O que cada status significa */}
                  <div>
                    <p className="text-dark-100 font-semibold text-sm mb-3">O que significa cada status?</p>
                    <div className="space-y-3">
                      {[
                        { dot: 'bg-emerald-400', label: 'Credenciais configuradas', desc: 'Todos os campos foram preenchidos.' },
                        { dot: 'bg-amber-400', label: 'Aguardando configuração', desc: 'Nenhuma credencial foi salva ainda.' },
                        { dot: 'bg-emerald-400 ring-2 ring-emerald-400/30', label: 'Conectado!', desc: 'Conexão testada e aprovada com sucesso.' },
                        { dot: 'bg-red-400', label: 'Falhou', desc: 'Verifique se as credenciais estão corretas e não expiraram.' },
                      ].map(s => (
                          <div key={s.label} className="flex items-start gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${s.dot}`} />
                            <div>
                              <span className="text-dark-200 text-sm font-medium">{s.label}</span>
                              <span className="text-dark-500 text-sm"> — {s.desc}</span>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>

                  {/* Links úteis */}
                  <div>
                    <p className="text-dark-100 font-semibold text-sm mb-3">Links úteis</p>
                    <div className="space-y-2">
                      {[
                        { label: 'Mercado Livre — Painel do Desenvolvedor', url: 'https://developers.mercadolivre.com.br' },
                        { label: 'Shopee — Open Platform', url: 'https://open.shopee.com' },
                        { label: 'Amazon — Seller Central', url: 'https://sellercentral.amazon.com.br' },
                        { label: 'Magazine Luiza — Seller Magalu', url: 'https://seller.magalu.com' },
                      ].map(link => (
                          <a
                              key={link.url}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                          >
                            <ArrowRight className="w-3 h-3 flex-shrink-0" />
                            {link.label}
                          </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-dark-700">
                  <button
                      onClick={() => setShowHelp(false)}
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors"
                  >
                    Entendi, vou configurar agora
                  </button>
                </div>
              </div>
            </>
        )}

        {/* User Code Info */}
        <div className="p-4 bg-dark-800/50 rounded-lg border border-dark-700">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-primary-400" />
            <div>
              <p className="text-sm text-dark-400">User Code Atual</p>
              <p className="text-dark-100 font-mono font-medium">{userCode}</p>
            </div>
          </div>
        </div>

        {/* Marketplaces Grid — helper para renderizar um card */}
        {(() => {
          const renderCard = (marketplace: typeof MARKETPLACES[0]) => {
            const configured = isMarketplaceConfigured(marketplace);
            return (
              <div
                  key={marketplace.id}
                  className={clsx(
                    'p-6 rounded-xl border transition-all',
                    configured
                      ? 'bg-primary-500/5 border-primary-500/50 hover:border-primary-500/70 shadow-sm shadow-primary-500/10'
                      : 'bg-dark-800 border-dark-700 hover:border-dark-600 border-dashed'
                  )}
              >
                {/* Marketplace Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div
                        className={clsx(
                          'w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center transition-all flex-shrink-0',
                          configured ? 'ring-1 ring-primary-500/30' : 'opacity-80'
                        )}
                    >
                      <MarketplaceLogo logoKey={marketplace.logo} name={marketplace.name} color={marketplace.color} />
                    </div>
                    <div>
                      <h3 className={clsx('text-lg font-bold', configured ? 'text-dark-100' : 'text-dark-300')}>
                        {marketplace.name}
                      </h3>
                      <p className={clsx('text-xs font-medium', configured ? 'text-primary-400' : 'text-dark-500')}>
                        {configured ? '✓ Configurado' : 'Sem credenciais'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {/* Criar Conta no Marketplace */}
                    {marketplace.registrationUrl && (
                        <a
                            href={marketplace.registrationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-xs rounded-lg transition-all flex items-center gap-1.5 font-medium border bg-dark-700 hover:bg-dark-600 text-white border-dark-600 shadow shadow-black/40"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Criar Conta no Marketplace
                        </a>
                    )}
                  </div>
                </div>

                {/* Credential Fields */}
                <div className="space-y-4">
                  {/* Botão OAuth Mercado Livre */}
                  {marketplace.id === 'ml' && (() => {
                    const mlConnected = mlOAuthStatus === 'success' || !!credentials.mlAccessToken;
                    return (
                      <div className="space-y-3">
                        {mlOAuthStatus === 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Mercado Livre conectado com sucesso!
                          </div>
                        )}
                        {mlOAuthStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            Erro na autorização: {mlOAuthError}
                          </div>
                        )}
                        {mlConnected && mlOAuthStatus !== 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Mercado Livre já conectado via OAuth.
                            {credentials.mlConnectedAt && (
                              <span className="text-emerald-500/70 text-xs ml-auto">
                                {new Date(credentials.mlConnectedAt as string).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={handleMLConnect}
                          disabled={mlOAuthStatus === 'connecting'}
                          className={clsx(
                            'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                            mlConnected
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                              : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30',
                            mlOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                          )}
                        >
                          {mlOAuthStatus === 'connecting' ? (
                            <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para Mercado Livre...</>
                          ) : mlConnected ? (
                            <><CheckCircle className="w-4 h-4" /> Reconectar Mercado Livre</>
                          ) : (
                            <>🤝 Autorizar QFlow no Mercado Livre (OAuth)</>
                          )}
                        </button>
                        <p className="text-xs text-dark-500 text-center">
                          Ou preencha os campos abaixo com token manual (legado).
                        </p>
                      </div>
                    );
                  })()}

                  {/* Botão OAuth Shopee */}
                  {marketplace.id === 'shopee' && (() => {
                    const shopeeConnected = shopeeOAuthStatus === 'success' || !!credentials.shopeeAccessToken || !!credentials.shopIdShopee;
                    return (
                      <div className="space-y-3">
                        {shopeeOAuthStatus === 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Shopee conectada com sucesso!
                          </div>
                        )}
                        {shopeeOAuthStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            Erro na autorização: {shopeeOAuthError}
                          </div>
                        )}
                        {shopeeConnected && shopeeOAuthStatus !== 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Shopee já conectada.
                            {credentials.shopeeConnectedAt && (
                              <span className="text-emerald-500/70 text-xs ml-auto">
                                {new Date(credentials.shopeeConnectedAt as string).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={handleShopeeConnect}
                          disabled={shopeeOAuthStatus === 'connecting'}
                          className={clsx(
                            'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                            shopeeConnected
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                              : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30',
                            shopeeOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                          )}
                        >
                          {shopeeOAuthStatus === 'connecting' ? (
                            <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para Shopee...</>
                          ) : shopeeConnected ? (
                            <><CheckCircle className="w-4 h-4" /> Reconectar Shopee</>
                          ) : (
                            <>🛍️ Autorizar QFlow na Shopee (OAuth)</>
                          )}
                        </button>
                        <p className="text-xs text-dark-500 text-center">
                          Você será redirecionado para a Shopee Open Platform para autorizar o QFlow.
                        </p>
                      </div>
                    );
                  })()}

                  {/* Botão OAuth Amazon */}
                  {marketplace.id === 'amazon' && (
                    <div className="space-y-3">
                      {/* Status banner */}
                      {amazonOAuthStatus === 'success' && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          Amazon conectada com sucesso! Seller ID salvo automaticamente.
                        </div>
                      )}
                      {amazonOAuthStatus === 'error' && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          Erro na autorização: {amazonOAuthError}
                        </div>
                      )}
                      {/* OAuth button */}
                      <button
                        onClick={handleAmazonConnect}
                        disabled={amazonOAuthStatus === 'connecting'}
                        className={clsx(
                          'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                          credentials.amazonSellerId
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                            : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30',
                          amazonOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        {amazonOAuthStatus === 'connecting' ? (
                          <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para Amazon...</>
                        ) : credentials.amazonSellerId ? (
                          <><CheckCircle className="w-4 h-4" /> Reconectar Amazon (Seller: {credentials.amazonSellerId})</>
                        ) : (
                          <>📦 Autorizar QFlow na Amazon (OAuth)</>
                        )}
                      </button>
                      <p className="text-xs text-dark-500 text-center">
                        Você será redirecionado para o Seller Central Amazon para autorizar o QFlow.
                      </p>
                    </div>
                  )}

                  {/* Botão OAuth Magalu */}
                  {marketplace.id === 'magalu' && (() => {
                    // Conectado se voltou do OAuth agora OU se já tem token salvo no banco
                    const magaluConnected = magaluOAuthStatus === 'success' || !!credentials.magaluAccessToken;
                    return (
                      <div className="space-y-3">
                        {magaluOAuthStatus === 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Magazine Luiza conectada com sucesso!
                          </div>
                        )}
                        {magaluOAuthStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            Erro na autorização. Tente novamente.
                          </div>
                        )}
                        {/* Banner de já-conectado (token no banco, não veio do OAuth agora) */}
                        {magaluConnected && magaluOAuthStatus !== 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            Magazine Luiza já conectada.
                            {credentials.magaluConnectedAt && (
                              <span className="text-emerald-500/70 text-xs ml-auto">
                                {new Date(credentials.magaluConnectedAt).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={handleMagaluConnect}
                          disabled={magaluOAuthStatus === 'connecting'}
                          className={clsx(
                            'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                            magaluConnected
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                              : 'bg-orange-500 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/30',
                            magaluOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                          )}
                        >
                          {magaluOAuthStatus === 'connecting' ? (
                            <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para Magalu...</>
                          ) : magaluConnected ? (
                            <><CheckCircle className="w-4 h-4" /> Reconectar Magazine Luiza</>
                          ) : (
                            <>💙 Autorizar QFlow na Magazine Luiza (OAuth)</>
                          )}
                        </button>
                        <p className="text-xs text-dark-500 text-center">
                          Você será redirecionado para o ID Magalu. Use as credenciais da sua loja (CNPJ).
                        </p>
                      </div>
                    );
                  })()}

                  {/* Botão OAuth TikTok Shop */}
                  {marketplace.id === 'tiktok' && (() => {
                    const tiktokConnected = tiktokOAuthStatus === 'success' || !!credentials.tiktokAccessToken;
                    return (
                      <div className="space-y-3">
                        {tiktokOAuthStatus === 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            TikTok Shop conectada com sucesso!
                          </div>
                        )}
                        {tiktokOAuthStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            Erro na autorização: {tiktokOAuthError}
                          </div>
                        )}
                        {tiktokConnected && tiktokOAuthStatus !== 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            TikTok Shop já conectada.
                            {credentials.tiktokSellerName && (
                              <span className="text-emerald-500/70 text-xs ml-auto">
                                {credentials.tiktokSellerName}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={handleTikTokConnect}
                          disabled={tiktokOAuthStatus === 'connecting'}
                          className={clsx(
                            'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                            tiktokConnected
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                              : 'bg-[#010101] text-white hover:bg-[#222] shadow-lg',
                            tiktokOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                          )}
                        >
                          {tiktokOAuthStatus === 'connecting' ? (
                            <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para TikTok...</>
                          ) : tiktokConnected ? (
                            <><CheckCircle className="w-4 h-4" /> Reconectar TikTok Shop</>
                          ) : (
                            <>🎵 Autorizar QFlow no TikTok Shop (OAuth)</>
                          )}
                        </button>
                        <p className="text-xs text-dark-500 text-center">
                          Você será redirecionado para o TikTok Shop Partner Center para autorizar o QFlow.
                        </p>
                      </div>
                    );
                  })()}

                  {/* Botão OAuth AliExpress */}
                  {marketplace.id === 'aliexpress' && (() => {
                    const aliConnected = aliexpressOAuthStatus === 'success' || !!credentials.aliexpressAccessToken;
                    return (
                      <div className="space-y-3">
                        {aliexpressOAuthStatus === 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            AliExpress conectado com sucesso!
                          </div>
                        )}
                        {aliexpressOAuthStatus === 'error' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            Erro na autorização: {aliexpressOAuthError}
                          </div>
                        )}
                        {aliConnected && aliexpressOAuthStatus !== 'success' && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            AliExpress já conectado.
                            {credentials.aliexpressSellerId && (
                              <span className="text-emerald-500/70 text-xs ml-auto">
                                Seller {credentials.aliexpressSellerId}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          onClick={handleAliExpressConnect}
                          disabled={aliexpressOAuthStatus === 'connecting'}
                          className={clsx(
                            'w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all',
                            aliConnected
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20'
                              : 'bg-[#FF4747] text-white hover:bg-[#e03030] shadow-lg',
                            aliexpressOAuthStatus === 'connecting' && 'opacity-60 cursor-not-allowed'
                          )}
                        >
                          {aliexpressOAuthStatus === 'connecting' ? (
                            <><Loader className="w-4 h-4 animate-spin" /> Redirecionando para AliExpress...</>
                          ) : aliConnected ? (
                            <><CheckCircle className="w-4 h-4" /> Reconectar AliExpress</>
                          ) : (
                            <>🔴 Autorizar QFlow no AliExpress (OAuth)</>
                          )}
                        </button>
                        <p className="text-xs text-dark-500 text-center">
                          Você será redirecionado para o AliExpress Open Platform para autorizar o QFlow.
                        </p>
                      </div>
                    );
                  })()}

                  {marketplace.fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-dark-300 mb-2">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </label>
                        <div className="relative">
                          <input
                              type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                              value={credentials[field.key as keyof MarketplaceCredentials] || ''}
                              onChange={(e) => updateCredential(field.key, e.target.value)}
                              className="w-full px-4 py-3 pr-12 bg-dark-900 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                              placeholder={field.type === 'password' ? '••••••••••••••••' : `Digite o ${field.label.toLowerCase()}`}
                          />

                          {field.type === 'password' && (
                              <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility(field.key)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-dark-500 hover:text-dark-300 transition-colors"
                              >
                                {showPasswords[field.key] ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                              </button>
                          )}
                        </div>
                      </div>
                  ))}
                </div>

                {/* Status Indicator */}
                <div className="mt-4 pt-4 border-t border-dark-700">
                  <div className="flex items-center gap-2 text-sm">
                    {marketplace.id === 'shopee' ? (
                      (shopeeOAuthStatus === 'success' || !!credentials.shopeeAccessToken || !!credentials.shopIdShopee) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-orange-400" />
                          <span className="text-orange-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.id === 'ml' ? (
                      (mlOAuthStatus === 'success' || !!credentials.mlAccessToken) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : credentials.tokenMercadoLivre ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Credenciais configuradas</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-yellow-400" />
                          <span className="text-yellow-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.id === 'magalu' ? (
                      (magaluOAuthStatus === 'success' || !!credentials.magaluAccessToken) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-blue-400" />
                          <span className="text-blue-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.id === 'amazon' ? (
                      (credentials.amazonSellerId || credentials.amazonConnectedAt) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          <span className="text-amber-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.id === 'tiktok' ? (
                      (tiktokOAuthStatus === 'success' || !!credentials.tiktokAccessToken) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          <span className="text-amber-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.id === 'aliexpress' ? (
                      (aliexpressOAuthStatus === 'success' || !!credentials.aliexpressAccessToken) ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Conectado via OAuth</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          <span className="text-amber-400">Aguardar autorização OAuth</span>
                        </>
                      )
                    ) : marketplace.fields.length > 0 && credentials[marketplace.fields[0].key] ? (
                        <>
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400">Credenciais configuradas</span>
                        </>
                    ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          <span className="text-amber-400">Aguardar configuração</span>
                        </>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          const connected    = MARKETPLACES.filter(m => isMarketplaceConfigured(m));
          const notConnected = MARKETPLACES.filter(m => !isMarketplaceConfigured(m));

          return (
            <div className="space-y-8">
              {/* ── Seção: Conectados ─────────────────────────────────────── */}
              {connected.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                    <h2 className="text-sm font-semibold text-primary-400 uppercase tracking-widest">
                      Conectados ({connected.length})
                    </h2>
                    <div className="flex-1 h-px bg-primary-500/20" />
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {connected.map(renderCard)}
                  </div>
                </div>
              )}

              {/* ── Seção: Para conectar ──────────────────────────────────── */}
              {notConnected.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 rounded-full bg-dark-500" />
                    <h2 className="text-sm font-semibold text-dark-400 uppercase tracking-widest">
                      Para conectar ({notConnected.length})
                    </h2>
                    <div className="flex-1 h-px bg-dark-700" />
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {notConnected.map(renderCard)}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Help Section */}
        <div className="p-6 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <div>
              <h4 className="text-dark-100 font-semibold mb-2">Como obter as credenciais?</h4>
              <ul className="space-y-2 text-sm text-dark-300">
                <li>• <strong>Mercado Livre:</strong> O User ID é o seu ID de vendedor. Encontre-o no seu perfil ou na App em <a href="https://developers.mercadolivre.com.br" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">developers.mercadolivre.com.br</a></li>
                <li>• <strong>Magazine Luiza:</strong> Entre em contacto com a equipa de integrações da Magalu</li>
                <li>• <strong>Casas Bahia:</strong> Aceda ao portal de sellers Via Varejo</li>
                <li>• <strong>Carrefour/Leroy:</strong> Obtenha o Shop ID e a API Key no painel Mirakl</li>
                <li>• <strong>Amazon:</strong> Aceda ao <a href="https://sellercentral.amazon.com.br" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Seller Central</a></li>
                <li>• <strong>Shopee:</strong> O Shop ID e Partner ID ficam na Shopee Open Platform</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="p-4 bg-dark-800/50 border border-dark-700 rounded-lg">
          <p className="text-xs text-dark-500 text-center">
            🔒 Todas as credenciais são armazenadas de forma segura e encriptadas no nosso cofre de chaves.
            Nunca partilhe os seus tokens com terceiros.
          </p>
        </div>
      </div>
  );
}
